import { create } from 'zustand';
import { Notification, NotificationCount } from '@/types';

/**
 * Notification state store using Zustand
 * Replaces Angular's DataService notification-related BehaviorSubjects
 */

interface NotificationState {
  // Notification list
  notifications: Notification[];

  // Counts
  totalCount: number;
  unreadCount: number;

  // Loading state
  isLoading: boolean;
}

interface NotificationActions {
  // Notification list actions
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: number) => void;

  // Count actions
  setCounts: (counts: NotificationCount) => void;
  setUnreadCount: (count: number) => void;
  decrementUnreadCount: () => void;

  // Loading actions
  setLoading: (isLoading: boolean) => void;

  // Clear all notification data
  clearNotifications: () => void;
}

type NotificationStore = NotificationState & NotificationActions;

export const useNotificationStore = create<NotificationStore>((set) => ({
  // Initial state
  notifications: [],
  totalCount: 0,
  unreadCount: 0,
  isLoading: false,

  // Notification list actions
  setNotifications: (notifications: Notification[]) => {
    const unreadCount = notifications.filter((n) => !n.isRead).length;
    set({
      notifications,
      totalCount: notifications.length,
      unreadCount,
      isLoading: false,
    });
  },

  addNotification: (notification: Notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      totalCount: state.totalCount + 1,
      unreadCount: notification.isRead ? state.unreadCount : state.unreadCount + 1,
    }));
  },

  markAsRead: (notificationId: number) => {
    set((state) => {
      const notification = state.notifications.find(
        (n) => n.notificationId === notificationId
      );

      if (!notification || notification.isRead) {
        return state;
      }

      return {
        notifications: state.notifications.map((n) =>
          n.notificationId === notificationId ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }));
  },

  removeNotification: (notificationId: number) => {
    set((state) => {
      const notification = state.notifications.find(
        (n) => n.notificationId === notificationId
      );

      return {
        notifications: state.notifications.filter(
          (n) => n.notificationId !== notificationId
        ),
        totalCount: Math.max(0, state.totalCount - 1),
        unreadCount:
          notification && !notification.isRead
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
      };
    });
  },

  // Count actions
  setCounts: (counts: NotificationCount) => {
    set({
      totalCount: counts.total,
      unreadCount: counts.unread,
    });
  },

  setUnreadCount: (count: number) => {
    set({ unreadCount: count });
  },

  decrementUnreadCount: () => {
    set((state) => ({
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },

  // Loading actions
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Clear all notification data
  clearNotifications: () => {
    set({
      notifications: [],
      totalCount: 0,
      unreadCount: 0,
      isLoading: false,
    });
  },
}));

/**
 * Selector hooks for specific state slices
 */
export const useNotifications = () =>
  useNotificationStore((state) => state.notifications);
export const useUnreadNotifications = () =>
  useNotificationStore((state) =>
    state.notifications.filter((n) => !n.isRead)
  );
export const useNotificationCount = () =>
  useNotificationStore((state) => state.totalCount);
export const useUnreadCount = () =>
  useNotificationStore((state) => state.unreadCount);
export const useNotificationLoading = () =>
  useNotificationStore((state) => state.isLoading);

export default useNotificationStore;
